
import { InventoryItem } from "@/types/inventory";

/**
 * Identifies problematic locations that should appear last in results
 */
const isProblematicLocation = (locatie: string | undefined): boolean => {
  if (!locatie) return false;
  const location = locatie.toLowerCase().trim();
  return ['ok', 'afroep', 'vervallen', 'zzz'].includes(location);
};

/**
 * Enhanced sorting for inventory results:
 * 1. First normal locations, then problematic locations (ok, afroep, vervallen, zzz)
 * 2. Within each location category: Goes (warehouse 2) first
 * 3. Then by stock availability (in-stock first)
 * 4. Finally alphabetically by name
 */
export const sortInventoryResults = (results: InventoryItem[]): InventoryItem[] => {
  return [...results].sort((a, b) => {
    // Priority 1: Location type (normal locations first, problematic last)
    const aIsProblematic = isProblematicLocation(a.locatie);
    const bIsProblematic = isProblematicLocation(b.locatie);
    
    if (aIsProblematic && !bIsProblematic) return 1; // a goes after b
    if (!aIsProblematic && bIsProblematic) return -1; // a goes before b
    
    // Priority 2: Goes warehouse (warehouse 2) comes first within same location category
    if (a.magazijn === 2 && b.magazijn !== 2) return -1;
    if (a.magazijn !== 2 && b.magazijn === 2) return 1;
    
    // Priority 3: Stock availability within same warehouse
    if ((a.voorraad > 0 && b.voorraad > 0) || (a.voorraad <= 0 && b.voorraad <= 0)) {
      // If stock status is the same, sort alphabetically by name
      return a.product_naam.localeCompare(b.product_naam);
    }
    
    // If product a is in stock, it comes first
    return a.voorraad > 0 ? -1 : 1;
  });
};

/**
 * Enhanced sorting specifically for color analysis matches
 * Prioritizes exact color and size matches, then considers location and warehouse
 */
export const sortColorAnalysisMatches = (matches: any[]): any[] => {
  return [...matches].sort((a, b) => {
    // Priority 1: Perfect matches (both color and size exact)
    const aIsPerfect = a.colorMatch && a.sizeMatch;
    const bIsPerfect = b.colorMatch && b.sizeMatch;
    
    if (aIsPerfect && !bIsPerfect) return -1;
    if (!aIsPerfect && bIsPerfect) return 1;
    
    // Priority 2: Color matches (most important for paint products)
    if (a.colorMatch && !b.colorMatch) return -1;
    if (!a.colorMatch && b.colorMatch) return 1;
    
    // Priority 3: Location type (avoid problematic locations)
    const aIsProblematic = isProblematicLocation(a.locatie);
    const bIsProblematic = isProblematicLocation(b.locatie);
    
    if (aIsProblematic && !bIsProblematic) return 1;
    if (!aIsProblematic && bIsProblematic) return -1;
    
    // Priority 4: Goes warehouse preference
    if (a.magazijn === 2 && b.magazijn !== 2) return -1;
    if (a.magazijn !== 2 && b.magazijn === 2) return 1;
    
    // Priority 5: Size match preference
    if (a.sizeMatch && !b.sizeMatch) return -1;
    if (!a.sizeMatch && b.sizeMatch) return 1;
    
    // Priority 6: Stock level (higher stock preferred)
    if (a.voorraad !== b.voorraad) {
      return b.voorraad - a.voorraad;
    }
    
    // Priority 7: Match score
    return (b.matchScore || 0) - (a.matchScore || 0);
  });
};

// Generate a unique key for inventory items in lists
export const generateInventoryItemKey = (item: InventoryItem, index: number): string => {
  return `${item.product_id}-${item.magazijn}-${item.locatie || 'no-loc'}-${index}`;
};
